import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

const MarketDataPanel = ({ bestBid, ltp, ltt, bestAsk, formatValue }) => {
  const theme = useTheme();

  // Helper to format LTT
  const formatLTT = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp.toString().length === 10 ? timestamp * 1000 : timestamp);
    return date.toLocaleTimeString('en-US', { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const items = [
    {
      label: "Best Bid",
      value: formatValue(bestBid),
      color: theme.palette.mode === "dark" ? "success.light" : "success.main",
      labelColor: "success.main",
    },
    {
      label: "LTP",
      value: formatValue(ltp),
      color: "primary.main",
      labelColor: "text.secondary",
    },
    {
      label: "LTT",
      value: formatLTT(ltt),
      color: "text.primary",
      labelColor: "text.secondary",
      isText: true, // Flag to indicate this is a text value, not currency
    },
    {
      label: "Best Ask",
      value: formatValue(bestAsk),
      color: theme.palette.mode === "dark" ? "error.light" : "error.main",
      labelColor: "error.main",
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: { xs: 0.5, sm: 1 },
        mb: 2,
        p: { xs: 1, sm: 1.5 },
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: "all 0.3s ease",
      }}
    >
      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 0.5,
            borderRadius: 1,
            transition: "all 0.3s ease",
            minHeight: { xs: "50px", sm: "64px" },
            backgroundColor: !item.value || item.value === "-"
              ? alpha(theme.palette.action.disabled, 0.1)
              : "transparent",
            "&:hover": {
              transform: item.value && item.value !== "-" ? "scale(1.02)" : "none",
              backgroundColor: item.value && item.value !== "-"
                ? alpha(theme.palette.primary.main, 0.1)
                : alpha(theme.palette.action.disabled, 0.1),
            },
          }}
        >
          {item.value && item.value !== "-" ? (
            <>
              <Typography
                variant="caption"
                color={item.labelColor}
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: { xs: 0, sm: 1 },
                  mb: 0.5,
                  fontSize: { xs: "0.6rem", sm: "0.75rem" },
                  textAlign: "center",
                  lineHeight: 1.2,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="body1"
                fontWeight="bold"
                color={item.color}
                sx={{
                  transition: "color 0.3s ease",
                  fontSize: { xs: "0.7rem", sm: "1rem" },
                  textAlign: "center",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                  "&:hover": {
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.text.primary
                        : theme.palette.text.secondary,
                  },
                }}
              >
                {item.isText ? item.value : `₹ ${item.value}`}
              </Typography>
            </>
          ) : (
            <Box
              sx={{
                width: "60%",
                height: "8px",
                backgroundColor: alpha(theme.palette.action.disabled, 0.2),
                borderRadius: "4px",
              }}
            />
          )}
        </Box>
      ))}
    </Paper>
  );
};

export default MarketDataPanel;


